import React, { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import "./SetPassword.css";
import authService from "../services/auth.service";

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const isPasswordValid = password.length >= 8;
  const isMatching = password === confirmPassword && confirmPassword !== "";
  const canSubmit = isPasswordValid && isMatching && status !== "loading";

  const strengthLevel =
    password.length === 0 ? 0 :
    password.length < 8 ? 1 :
    password.length < 12 ? 2 : 3;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid or expired reset link.");
      return;
    }

    if (!isPasswordValid) {
      setStatus("error");
      setErrorMessage("Password must be at least 8 characters.");
      return;
    }

    if (!isMatching) {
      setStatus("error");
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      setStatus("loading");
      await authService.setPassword({ token, new_password: password });
      setStatus("success");
    } catch (error: unknown) {
      setStatus("error");
      let message = "Something went wrong. Please try again.";
      if (typeof error === "object" && error !== null) {
        const err = error as any;
        if (err.response?.data?.message) message = err.response.data.message;
        else if (err.message) message = err.message;
      }
      setErrorMessage(message);
    }
  };

  // ❌ No token
  if (!token) {
    return (
      <div className="sp-page">
        <div className="sp-card">
          <div className="sp-icon-box sp-icon-error">
            <AlertCircle size={28} strokeWidth={1.5} />
          </div>
          <h2 className="sp-title">Invalid Link</h2>
          <p className="sp-subtitle">This link is invalid or has expired. Please request a new one.</p>
          <button className="sp-btn" onClick={() => navigate("/login")}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  // ✅ Success
  if (status === "success") {
    return (
      <div className="sp-page">
        <div className="sp-card">
          <div className="sp-icon-box sp-icon-success">
            <CheckCircle2 size={28} strokeWidth={1.5} />
          </div>
          <h2 className="sp-title">Password Set!</h2>
          <p className="sp-subtitle">Your account is now active. You can log in with your new password.</p>
          <button className="sp-btn" onClick={() => navigate("/login")}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-page">
      <div className="sp-card">

        <div className="sp-icon-box sp-icon-primary">
          <Lock size={28} strokeWidth={1.5} />
        </div>

        <h2 className="sp-title">Create Password</h2>
        <p className="sp-subtitle">Choose a strong password to activate your account.</p>

        <form onSubmit={handleSubmit} noValidate>

          {status === "error" && (
            <div className="sp-error-box">
              <AlertCircle size={16} />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="sp-field">
            <label className="sp-label">New Password</label>
            <div className="sp-input-wrap">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setStatus("idle"); }}
                placeholder="At least 8 characters"
                className={`sp-input ${status === "error" && !isPasswordValid ? "sp-input-error" : ""}`}
                required
              />
              <button type="button" className="sp-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="sp-strength-bar">
              <div className={`sp-strength-seg ${strengthLevel >= 1 ? "sp-weak" : ""}`}></div>
              <div className={`sp-strength-seg ${strengthLevel >= 2 ? "sp-medium" : ""}`}></div>
              <div className={`sp-strength-seg ${strengthLevel >= 3 ? "sp-strong" : ""}`}></div>
            </div>
            {password.length > 0 && (
              <span className={`sp-strength-label sp-strength-label-${["", "weak", "medium", "strong"][strengthLevel]}`}>
                {["", "Too short", "Good", "Strong"][strengthLevel]}
              </span>
            )}
          </div>

          <div className="sp-field">
            <label className="sp-label">Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setStatus("idle"); }}
              placeholder="Repeat your password"
              className={`sp-input ${confirmPassword && !isMatching ? "sp-input-error" : ""}`}
              required
            />
            {confirmPassword && !isMatching && (
              <span className="sp-error-text">Passwords do not match</span>
            )}
          </div>

          <button type="submit" className="sp-btn" disabled={!canSubmit}>
            {status === "loading" ? (
              <span className="sp-btn-loading">
                <Loader2 size={18} className="sp-spin" />
                Saving...
              </span>
            ) : (
              "Set Password"
            )}
          </button>
        </form>

        <div className="sp-footer">
          Secure · Encrypted · Protected
        </div>
      </div>
    </div>
  );
}
